import { useEffect, useRef, useState } from 'react';

import './style.scss';

/*
 * Sentinels will render with no content at top and bottom of the container.
 * The will intersect with the viewport, so we know when the sticky item is being stuck.
 * For this, we will use the position in the y axis
 */
const createSentinels = elem => {
  const topSentinel = document.createElement('div');
  topSentinel.classList.add('sticky-sentinel', 'sticky-sentinel--top');
  elem.parentElement.appendChild(topSentinel);

  const bottomSentinel = document.createElement('div');
  bottomSentinel.classList.add('sticky-sentinel', 'sticky-sentinel--bottom');
  elem.parentElement.appendChild(bottomSentinel);

  return { topSentinel, bottomSentinel };
};

export const useIsElementStuck = () => {
  const [isStuck, setIsStuck] = useState(false);
  const elem = useRef(null);

  useEffect(() => {
    if (elem?.current) {
      const { topSentinel, bottomSentinel } = createSentinels(elem.current);
      const topObserver = new IntersectionObserver(
        ([topSentinelEntry]) => {
          const sentinelPosition = topSentinelEntry.boundingClientRect.bottom;
          const rootTop = topSentinelEntry.rootBounds.top;
          const rootBottom = topSentinelEntry.rootBounds.bottom;

          // When the sentinel position is above (lower than) the top of the viewport (0), we know it is stuck
          if (sentinelPosition < rootTop) {
            setIsStuck(true);
          }

          // If the sentinel position is below (greater than) the top of the viewport, it means we have not crossed the boundary
          if (sentinelPosition >= rootTop && sentinelPosition < rootBottom) {
            setIsStuck(false);
          }
        },
        {
          threshold: [1]
        }
      );

      const bottomObserver = new IntersectionObserver(
        ([bottomSentinelEntry]) => {
          const sentinelPosition =
            bottomSentinelEntry.boundingClientRect.bottom;
          const rootTop = bottomSentinelEntry.rootBounds.top;
          const rootBottom = bottomSentinelEntry.rootBounds.bottom;

          /*
           * 2 options to say the element is stuck
           * - The sentinel at bottom is below the bottom of the viewport and the top sentinel has crossed the boundary yet
           * - The sentinel at bottom is below the top of the viewport and above the bottom of the viewport.
           */
          if (
            (sentinelPosition > rootBottom &&
              topSentinel.getBoundingClientRect().top < rootTop) ||
            (sentinelPosition > rootTop && sentinelPosition < rootBottom)
          ) {
            setIsStuck(true);
          }

          if (sentinelPosition < rootTop && sentinelPosition < rootBottom) {
            setIsStuck(false);
          }
        },
        {
          threshold: [1]
        }
      );

      topObserver.observe(topSentinel);
      bottomObserver.observe(bottomSentinel);
      return () => {
        topObserver.unobserve(topSentinel);
        bottomObserver.unobserve(bottomSentinel);
      };
    }

    return () => {};
  }, [elem.current]);

  return {
    elem,
    isStuck
  };
};

export default { useIsElementStuck };
